/**
 * Feature E02-F01-T06 — "New Project" create flow typed api-client + Query hook.
 *
 * Feature-owned (the shared `api/queries.ts` only has the LIST hooks). Uses the
 * shared `apiClient` (auto-injects the session Bearer token, normalizes the
 * response, never throws) + `unwrap` (the single throw-at-the-Query-boundary
 * helper). The reply is validated against the shared
 * `createManualProjectResponseSchema` so the SPA speaks the exact contract the
 * Fastify route returns.
 *
 * 1:1 port of `../EER app/public/assets/js/dashboard.js → createManualProject`:
 *   POST /projects  (command: create_manual)  → { success, project_id, error }
 *
 * The create_manual endpoint takes NO client-controlled body fields (owner_id is
 * the session identity, role_name/stage/status are server-fixed — IDOR safe), so
 * the mutation posts an empty body. On success the mutation invalidates the
 * `projects` domain so the dashboard list refetches and the new project appears.
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { createManualProjectResponseSchema, type CreateManualProjectResponse } from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';
import { qk } from '../api/queryKeys.js';

/** POST /projects (create_manual) — create an empty stage-1 project. */
export async function createManualProject(): Promise<CreateManualProjectResponse> {
  const result = await apiClient.post<unknown>('/projects', {}, { command: 'create_manual' });
  return createManualProjectResponseSchema.parse(unwrap(result));
}

/**
 * Create-manual mutation. On success it invalidates the whole `projects` domain
 * prefix (list + any detail) so the dashboard refetches and the new project is
 * visible; the caller reads `{ success, project_id }` and opens the new project.
 */
export function useCreateManualProject(): UseMutationResult<
  CreateManualProjectResponse,
  ApiResultError,
  void
> {
  const queryClient = useQueryClient();
  return useMutation<CreateManualProjectResponse, ApiResultError, void>({
    mutationFn: () => createManualProject(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.projects.all });
    },
  });
}
